import * as React from 'react';
import { Tooltip, theme } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useCommonInfoStore } from '@src/shared/store';
import { checkTimeIntervalAvailable } from '@src/shared/utils';
import DatePicker from '@src/shared/components/DatePicker';
import GlobalFilter from '@src/features/GlobalFilter';
import * as store from '../store';
import DashboardSettings from '../Settings';
import Analysis from './Analysis';
import SavePopconfirm from './SavePopconfirm';
import SharePopover from './SharePopover';
import * as Styles from './styled';

const INVALID_EVENTS_ALERT = `One or more funnel steps has been deleted. The system has re-rendered the analysis to reflect this change.`;
const INVALID_INTERVAL_ALERT = {
  outOfRange: 'Selected time range exceeds the data retention period. No data is available.',
  overlap: 'The selected period overlaps partially with available data. Results may be incomplete.',
};

const Dashboard = () => {
  const defaultTimeRange = useCommonInfoStore((s) => s.timeRange);
  const customerId = useCommonInfoStore((s) => s.userInfo.customer_id);
  const interval = store.useDashboardInterval();
  const rawFilters = store.useDashboardRawFilters();
  const rawPattern = store.useDashboardRawPattern();
  const pattern = store.useDashboardPattern();
  const onChange = store.useOnChange();
  const { token } = theme.useToken();

  const handleIntervalChange = React.useCallback(
    (value: [string, string]) => {
      onChange((d) => {
        d.time_range = value;
      });
    },
    [onChange],
  );

  const handleFilterChange = React.useCallback(
    (value) => {
      onChange((d) => {
        d.filters = value.map((group) =>
          group.map((item) => ({
            field: item.field,
            op: item.op,
            val: item.val,
            label: item.label,
          })),
        );
      });
    },
    [onChange],
  );

  const invalidEventNumber = rawPattern.length - pattern.length;
  const intervalAvailable = checkTimeIntervalAvailable(interval, defaultTimeRange);

  return (
    <Styles.Container>
      {!!invalidEventNumber && (
        <Styles.DashboardAlert type="error" showIcon closable message={INVALID_EVENTS_ALERT} />
      )}
      <Styles.Header>
        <GlobalFilter
          globalFilter={rawFilters}
          interval={interval}
          onFilterChange={handleFilterChange}
        />
        <Styles.HeaderControls>
          {intervalAvailable !== 'available' && (
            <Tooltip title={INVALID_INTERVAL_ALERT[intervalAvailable]}>
              <WarningOutlined style={{ color: token.colorWarning, fontSize: 20 }} />
            </Tooltip>
          )}
          <DatePicker
            value={interval}
            range={defaultTimeRange}
            maxLength={
              customerId === 1960188863
                ? 30 * 24 * 60 * 60 * 1000
                : customerId === 1960188707
                  ? 7 * 24 * 60 * 60 * 1000
                  : 3 * 24 * 60 * 60 * 1000
            }
            status={intervalAvailable === 'available' ? undefined : 'warning'}
            styles={{ root: { marginRight: 12 } }}
            onChange={handleIntervalChange}
          />
          <SavePopconfirm />
          <SharePopover />
        </Styles.HeaderControls>
      </Styles.Header>
      <Styles.ContentPanel
        minSize={550}
        left={<DashboardSettings />}
        right={<Analysis />}
        styles={{
          left: { background: '#fff' },
        }}
      />
    </Styles.Container>
  );
};

export default React.memo(Dashboard);
